import { useRouter } from "expo-router";
import { useAtomValue } from "jotai";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import type { DisplayEvent } from "@/lib/event-helpers";
import type { EventGuest } from "@/lib/event-queries";

import { sessionAtom } from "@/atoms/session";
import AddToCalendar from "@/components/add-to-calendar";
import BottomEventButton from "@/components/BottomEventButton";
import { GText } from "@/components/g-text";
import GuestListModal from "@/components/guest-list-modal";
import OpenDirections from "@/components/open-directions";
import RsvpModal from "@/components/rsvp-modal";
import { SectionEditModal } from "@/components/section-edit-modal";
import SignInModal from "@/components/sign-in/sign-in-modal";
import { CalendarIcon } from "@/components/ui/calendar-icon";
import { PinIcon } from "@/components/ui/pin-icon";
import { TicketIcon } from "@/components/ui/ticket-icon";
import { colors } from "@/constants/colors";
import Bugsnag from "@/lib/bugsnag";
import { upsertRsvpStatus } from "@/lib/event-queries";
import { ACCEPT_STRING, validateAndUploadImage } from "@/lib/image-upload";
import {
  composeAddress,
  formatEventDate,
  formatGuestList,
  formatHostsBeingShown,
  formatPriceEditMode,
  formatPriceViewMode,
  formatTimeRange,
} from "@/utils/copy";

const HEADER_HEIGHT = 60;

const HORIZONTAL_MARGIN = 10;
// ── Types ────────────────────────────────────────────────────────────

export interface EventFormValues {
  currency: string;
  description: string;
  duration_minutes: string;
  flyer_url: string;
  location_city: string;
  location_country: string;
  location_formatted_address: string;
  location_lat: string;
  location_lng: string;
  location_name: string;
  location_neighborhood: string;
  location_place_id: string;
  location_postal_code: string;
  location_state: string;
  location_street: string;
  price_cents: string;
  price_service_fee: string;
  quantity_total: string;
  starts_at: string;
  status: string;
  ticket_description: string;
  ticket_name: string;
  timezone: string;
  title: string;
  visibility: string;
}

export const EMPTY_FORM: EventFormValues = {
  currency: "usd",
  description: "",
  duration_minutes: "120",
  flyer_url: "",
  location_city: "",
  location_country: "US",
  location_formatted_address: "",
  location_lat: "",
  location_lng: "",
  location_name: "",
  location_neighborhood: "",
  location_place_id: "",
  location_postal_code: "",
  location_state: "",
  location_street: "",
  price_cents: "1000",
  price_service_fee: "0",
  quantity_total: "10",
  starts_at: "",
  status: "draft",
  ticket_description: "",
  ticket_name: "",
  timezone: "America/New_York",
  title: "",
  visibility: "public",
};

interface EventDisplayProps {
  event?: DisplayEvent;
  guests: EventGuest[];
  hosts: string[];
  initialValues?: Partial<EventFormValues>;
  mode: "create" | "edit" | "view";
  myRsvp: { role: string; rsvp_status: string } | null;
  onFlyerUrlChange?: (url: string) => void;
  onGetTicket?: () => void;
  onSave?: (values: EventFormValues) => Promise<void>;
  onShareEvent?: () => void;
  paymentUI?: React.ReactNode;
  submitLabel?: string;
  ticketLoading?: boolean;
}

// ── Helpers ──────────────────────────────────────────────────────────

export function canPublish(values: EventFormValues): boolean {
  return !!(
    values.title.trim() &&
    values.starts_at &&
    values.duration_minutes &&
    values.location_name.trim()
  );
}

export function EventDisplay({
  event,
  guests,
  hosts,
  initialValues,
  mode,
  myRsvp,
  onFlyerUrlChange,
  onGetTicket,
  onSave,
  onShareEvent,
  paymentUI,
  ticketLoading,
}: EventDisplayProps) {
  const insets = useSafeAreaInsets();
  const vScale = 1;
  const iconSize = 32;
  const iconColWidth = 40;
  const router = useRouter();
  const sessionState = useAtomValue(sessionAtom);
  const session = sessionState === "loading" ? null : sessionState;
  const editable = mode !== "view";
  const viewCursor =
    Platform.OS === "web" ? { cursor: "default" as const } : {};

  // State for edit/create
  const [values, setValues] = useState<EventFormValues>(() => ({
    ...EMPTY_FORM,
    ...initialValues,
  }));
  const [modalSection, setModalSection] = useState<
    "date" | "description" | "location" | "ticket" | "title" | null
  >(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [guestListVisible, setGuestListVisible] = useState(false);
  const [rsvpVisible, setRsvpVisible] = useState(false);
  const [signInVisible, setSignInVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-detect timezone for new events
  const detectTimezone = useCallback(() => {
    if (editable && !initialValues?.timezone) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setValues((v) => ({ ...v, timezone: tz }));
    }
  }, [editable, initialValues?.timezone]);

  useEffect(() => {
    detectTimezone();
  }, [detectTimezone]);

  // Sync local form values when exiting edit mode (e.g. after publish)
  useEffect(() => {
    if (!editable && initialValues) {
      setValues((v) => ({ ...v, ...initialValues }));
    }
  }, [editable, initialValues]);

  const myStatus = myRsvp?.rsvp_status ?? null;
  const isAdmin = myRsvp?.role === "admin";

  const patchValues = (patch: Partial<EventFormValues>) =>
    setValues((v) => ({ ...v, ...patch }));

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    try {
      const url = await validateAndUploadImage(
        file,
        session!.access_token,
        (p) => setUploadProgress(p),
      );
      patchValues({ flyer_url: url });
      onFlyerUrlChange?.(url);
    } catch (err) {
      Bugsnag.notify(err instanceof Error ? err : new Error(String(err)));
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = async (overrides?: Partial<EventFormValues>) => {
    if (!onSave) return;
    const finalValues = { ...values, ...overrides, ends_at: "" };
    if (finalValues.starts_at) {
      const start = DateTime.fromISO(finalValues.starts_at);
      if (start.isValid) {
        const dur = parseInt(finalValues.duration_minutes, 10) || 120;
        finalValues.ends_at = start.plus({ minutes: dur }).toISO()!;
      }
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(finalValues);
    } catch (e: unknown) {
      Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleRsvp = async (choice: "cancelled" | "confirmed" | "maybe") => {
    setRsvpVisible(false);
    if (!event || !session) return;
    await upsertRsvpStatus(event.id, session.user.id, choice);
  };

  // Derive display values — view mode uses event, edit/create uses form values
  const displayTitle = mode === "view" ? event!.title : values.title;
  const displayStartISO = mode === "view" ? event!.startISO : values.starts_at;
  const displayEndISO =
    mode === "view"
      ? event!.endISO
      : (() => {
          const start = DateTime.fromISO(values.starts_at);
          if (!start.isValid) return "";
          const dur = parseInt(values.duration_minutes, 10) || 120;
          return start.plus({ minutes: dur }).toISO()!;
        })();
  const hasTicket =
    mode === "view"
      ? event?.ticketKindId != null
      : !!initialValues?.price_cents && initialValues.price_cents !== "0";
  const isConfirmed = mode === "view" && myStatus === "confirmed";
  const displayVenue =
    mode === "view"
      ? isConfirmed
        ? event!.venue.toLowerCase()
        : [event!.locationNeighborhood, event!.locationCity]
            .filter(Boolean)
            .join(", ")
            .toLowerCase() || event!.venue.toLowerCase()
      : (values.location_name || "location").toLowerCase();
  const displayAddress =
    mode === "view"
      ? isConfirmed
        ? event!.address
        : "address shown after rsvp"
      : composeAddress(values) || "add address";
  const displayPrice =
    mode === "view"
      ? formatPriceViewMode(event!.priceCents, event!.priceServiceFee)
      : values.price_cents === "0"
        ? "free"
        : `you'll get ${formatPriceEditMode(values.price_cents)} per person`;
  const displayPriceDesc =
    mode === "view"
      ? event!.ticketsRemaining != null && event!.ticketsRemaining < 20
        ? `${event!.ticketsRemaining} left`
        : event!.priceServiceFee > 0
          ? `includes $${event!.priceServiceFee % 100 === 0 ? event!.priceServiceFee / 100 : (event!.priceServiceFee / 100).toFixed(2)} service cost`
          : event!.priceDescription
      : values.price_cents && values.price_cents !== "0"
        ? "a service fee will be added to cover processing costs"
        : null;
  const displayDescription =
    mode === "view" ? event!.description : values.description;
  const _displayGeo =
    mode === "view"
      ? event!.geo
      : {
          lat: parseFloat(values.location_lat) || 0,
          lng: parseFloat(values.location_lng) || 0,
        };

  // Info row action text
  const dateAction = mode === "view" ? "add to calendar" : "edit";
  const venueAction =
    mode === "view"
      ? isConfirmed
        ? "directions"
        : hasTicket
          ? "get ticket"
          : "rsvp"
      : "edit";
  const priceAction =
    mode === "view" ? (isConfirmed ? "share event" : "get ticket") : "edit";

  // Wrap info rows for view mode actions vs edit mode modals
  const wrapDateRow = (children: React.ReactNode) => {
    if (mode === "view" && event) {
      return (
        <AddToCalendar
          description={event.description}
          endISO={event.endISO ?? ""}
          geo={event.geo}
          location={event.address}
          startISO={event.startISO ?? ""}
          title={event.title}
          venue={event.venue}
        >
          {children}
        </AddToCalendar>
      );
    }
    return (
      <Pressable
        onPress={() => {
          setModalSection("date");
        }}
        style={({ pressed }: { hovered?: boolean; pressed: boolean }) => [
          pressed && styles.rowHighlight,
        ]}
      >
        {children}
      </Pressable>
    );
  };

  const wrapVenueRow = (children: React.ReactNode) => {
    if (mode === "view" && event) {
      if (isConfirmed) {
        return (
          <OpenDirections
            label={event.venue}
            lat={event.geo.lat}
            lng={event.geo.lng}
            placeId={event.placeId}
          >
            {children}
          </OpenDirections>
        );
      }
      return (
        <Pressable
          onPress={hasTicket ? onGetTicket : () => setRsvpVisible(true)}
        >
          {children}
        </Pressable>
      );
    }
    return (
      <Pressable
        onPress={() => {
          setModalSection("location");
        }}
        style={({ pressed }: { hovered?: boolean; pressed: boolean }) => [
          pressed && styles.rowHighlight,
        ]}
      >
        {children}
      </Pressable>
    );
  };

  const wrapPriceRow = (children: React.ReactNode) => {
    if (mode === "view") {
      return (
        <Pressable onPress={isConfirmed ? onShareEvent : onGetTicket}>
          {children}
        </Pressable>
      );
    }
    return (
      <Pressable
        onPress={() => {
          setModalSection("ticket");
        }}
        style={({ pressed }: { hovered?: boolean; pressed: boolean }) => [
          pressed && styles.rowHighlight,
        ]}
      >
        {children}
      </Pressable>
    );
  };

  const getBottomLabel = useMemo(() => {
    console.log("event form values", values.status);
    if (mode === "view") {
      if (!session) return "login";
      if (isAdmin && values.status !== "published") return "continue editing";
      if (ticketLoading) return "processing...";

      if (!isConfirmed) {
        if (hasTicket) {
          if (isAdmin) {
            return "rsvp";
          }
          return "get ticket";
        }
        return "rsvp";
      }
      return "share event";
    }
    if (!values.title.trim()) return "add title";
    if (!values.starts_at || !values.duration_minutes) return "add date";
    if (!values.location_name.trim()) return "add location";
    if (values.status !== "published") return "publish event";
    return "finish with edits";
  }, [
    hasTicket,
    isAdmin,
    isConfirmed,
    mode,
    session,
    ticketLoading,
    values.duration_minutes,
    values.location_name,
    values.starts_at,
    values.status,
    values.title,
  ]);

  const handleBottomOnPress = () => {
    switch (getBottomLabel) {
      case "add date":
        setModalSection("date");
        break;
      case "add location":
        setModalSection("location");
        break;
      case "add title":
        setModalSection("title");
        break;
      case "continue editing":
        router.setParams({ mode: "edit" });
        break;
      case "finish with edits":
        router.setParams({ mode: undefined });
        break;
      case "get ticket":
        onGetTicket?.();
        break;
      case "login":
        setSignInVisible(true);
        break;
      case "publish event":
        handleSave({ status: "published" });
        break;
      case "rsvp":
        setRsvpVisible(true);
        break;
      case "share event":
        onShareEvent?.();
        break;
    }
  };

  return (
    <View style={styles.container}>
      {/* Fixed header */}
      <View style={styles.fixedHeader}>
        {editable ? (
          <>
            <Pressable onPress={() => router.setParams({ mode: undefined })}>
              <GText style={styles.cancelText}>cancel</GText>
            </Pressable>

            <View style={styles.statusPillRow}>
              {[
                ...(initialValues?.status === "draft"
                  ? ["draft" as const]
                  : []),
                "published" as const,
                "cancelled" as const,
              ].map((s) => {
                const isActive = values.status === s;
                const originalStatus = initialValues?.status ?? "draft";
                const isDisabled =
                  (s === "draft" && originalStatus !== "draft") ||
                  (originalStatus === "cancelled" && s !== "cancelled") ||
                  (s === "published" && !canPublish(values));

                let shownStatusLabel: string = s;
                switch (s) {
                  case "cancelled":
                    shownStatusLabel = "cancel event";
                    break;
                  case "draft":
                    shownStatusLabel = "edit mode";
                    break;
                  case "published":
                    if (values.status === "published") {
                      shownStatusLabel = "published";
                    } else {
                      shownStatusLabel = "publish";
                    }
                    break;
                }

                return (
                  <Pressable
                    disabled={isDisabled || isActive}
                    key={s}
                    onPress={() => patchValues({ status: s })}
                    style={[
                      styles.statusPill,
                      isActive &&
                        (s === "cancelled"
                          ? styles.statusPillCancelled
                          : styles.statusPillActive),
                      isDisabled && styles.statusPillDisabled,
                    ]}
                  >
                    <GText
                      style={[
                        styles.statusPillText,
                        isActive && styles.statusPillTextActive,
                        isActive &&
                          s === "cancelled" &&
                          styles.statusPillTextCancelled,
                      ]}
                    >
                      {shownStatusLabel}
                    </GText>
                  </Pressable>
                );
              })}
            </View>

            <View style={styles.headerSpacer} />
          </>
        ) : (
          <>
            <Pressable
              onPress={() => router.navigate("/")}
              style={[styles.editButton, { marginRight: "auto" }]}
            >
              <GText style={styles.editText}>see all events</GText>
            </Pressable>
            <View style={styles.headerSpacer} />

            {isAdmin && (
              <Pressable
                onPress={() => router.setParams({ mode: "edit" })}
                style={[styles.editButton, { marginLeft: "auto" }]}
              >
                <GText style={styles.editText}>edit</GText>
              </Pressable>
            )}
          </>
        )}
      </View>

      <ScrollView
        contentContainerStyle={[
          styles.content,
          {
            backgroundColor: colors.darkOverlay,
            borderRadius: 24,
            marginBottom: 100,
            marginTop: HEADER_HEIGHT,
            maxWidth: 600,
            padding: 20,

            paddingBottom: insets.bottom,
          },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        style={[styles.scroll]}
      >
        {/* Title */}
        <View style={styles.headerRow}>
          <Pressable
            disabled={!editable}
            onPress={() => setModalSection("title")}
          >
            <GText
              nativeID="event-title"
              style={[
                styles.title,
                !editable && viewCursor,
                editable && { color: colors.white },
                editable && !values.title && { opacity: 0.5 },
              ]}
            >
              {editable ? values.title || "title" : displayTitle}
            </GText>
          </Pressable>
        </View>

        {/* Hosted by */}
        {hosts.length > 0 && (
          <GText style={[styles.hostedBy, viewCursor]}>
            {formatHostsBeingShown(hosts)}
          </GText>
        )}

        {/* Info grid */}
        <View style={styles.infoGrid}>
          {/* Date row */}
          {wrapDateRow(
            <View style={[styles.infoRow, { marginBottom: 12 * vScale }]}>
              <View style={[styles.iconColumn, { width: iconColWidth }]}>
                <CalendarIcon color={colors.white} size={iconSize} />
              </View>
              <View style={styles.textColumn}>
                <GText
                  style={[styles.detailPrimary, { fontSize: 15 * vScale }]}
                >
                  {displayStartISO ? formatEventDate(displayStartISO) : "date"}
                </GText>
                {displayStartISO ? (
                  <GText
                    style={[styles.detailSecondary, { fontSize: 14 * vScale }]}
                  >
                    {formatTimeRange(displayStartISO, displayEndISO)}
                  </GText>
                ) : null}
              </View>
              <GText style={[styles.actionText, { fontSize: 14 * vScale }]}>
                {dateAction}
              </GText>
            </View>,
          )}

          {/* Venue row */}
          {wrapVenueRow(
            <View style={[styles.infoRow, { marginBottom: 12 * vScale }]}>
              <View style={[styles.iconColumn, { width: iconColWidth }]}>
                <PinIcon color={colors.white} size={iconSize} />
              </View>
              <View style={styles.textColumn}>
                <GText
                  style={[styles.detailPrimary, { fontSize: 15 * vScale }]}
                >
                  {displayVenue}
                </GText>
                <GText
                  style={[styles.detailSecondary, { fontSize: 14 * vScale }]}
                >
                  {displayAddress}
                </GText>
              </View>
              <GText style={[styles.actionText, { fontSize: 14 * vScale }]}>
                {venueAction}
              </GText>
            </View>,
          )}

          {/* Price row — hidden when no ticket exists */}
          {!hasTicket
            ? null
            : wrapPriceRow(
                <View style={[styles.infoRow, { marginBottom: 12 * vScale }]}>
                  <View style={[styles.iconColumn, { width: iconColWidth }]}>
                    <TicketIcon color={colors.white} size={iconSize} />
                  </View>
                  <View style={styles.textColumn}>
                    <GText
                      style={[styles.detailPrimary, { fontSize: 15 * vScale }]}
                    >
                      {displayPrice}
                    </GText>
                    {displayPriceDesc ? (
                      <GText
                        style={[
                          styles.detailSecondary,
                          { fontSize: 14 * vScale },
                        ]}
                      >
                        {displayPriceDesc}
                      </GText>
                    ) : null}
                  </View>
                  <GText style={[styles.actionText, { fontSize: 14 * vScale }]}>
                    {priceAction}
                  </GText>
                </View>,
              )}
        </View>

        {/* Visibility + RSVP row */}
        <View style={styles.statusRow}>
          {editable ? (
            <>
              <GText style={styles.statusLabel}>event visibility:</GText>
              <View style={styles.statusPillRow}>
                {(
                  [
                    { label: "open to all", value: "public" },
                    {
                      label: "invite only",
                      value: "invite_only",
                    },
                  ] as const
                ).map((opt) => {
                  const isActive = values.visibility === opt.value;
                  return (
                    <Pressable
                      disabled={isActive}
                      key={opt.value}
                      onPress={() => patchValues({ visibility: opt.value })}
                      style={[
                        styles.statusPill,
                        isActive && styles.statusPillActive,
                      ]}
                    >
                      <GText
                        style={[
                          styles.statusPillText,
                          isActive && styles.statusPillTextActive,
                        ]}
                      >
                        {opt.label}
                      </GText>
                    </Pressable>
                  );
                })}
              </View>
              <GText style={[styles.statusLabel, { opacity: 0 }]}>
                event visibility:
              </GText>
            </>
          ) : (
            <>
              {session ? (
                guests.length > 0 ? (
                  <Pressable
                    onPress={() => setGuestListVisible(true)}
                    style={{ flex: 1 }}
                  >
                    <GText style={styles.rsvpNames}>
                      {formatGuestList(guests)}
                    </GText>
                  </Pressable>
                ) : (
                  <View style={{ flex: 1 }} />
                )
              ) : (
                <Pressable
                  onPress={() => setSignInVisible(true)}
                  style={{ flex: 1 }}
                >
                  <GText style={styles.rsvpNames}>
                    login to see who&apos;s going
                  </GText>
                </Pressable>
              )}
              <GText style={{ color: colors.light, ...viewCursor }}>
                {values.visibility === "invite_only"
                  ? "invitation only"
                  : "public event"}
              </GText>
            </>
          )}
        </View>

        {/* Description */}
        <View style={[styles.section, { marginBottom: 24 * vScale }]}>
          <Pressable
            disabled={!editable}
            onPress={() => setModalSection("description")}
          >
            <GText
              style={[
                styles.description,
                { fontSize: 15 * vScale, lineHeight: 22 * vScale },
                !editable && viewCursor,
                { color: editable ? colors.white : colors.light },
              ]}
            >
              {editable
                ? values.description || "add description"
                : displayDescription.replace(/\\n/g, "\n")}
            </GText>
          </Pressable>
        </View>

        {/* Flyer link */}
        {editable && (
          <>
            <Pressable
              disabled={uploading}
              onPress={() => fileInputRef.current?.click()}
            >
              <GText style={styles.flyerLinkText}>
                {uploading
                  ? `uploading ${uploadProgress}%`
                  : values.flyer_url
                    ? "+ edit background image"
                    : "+ add background image"}
              </GText>
            </Pressable>
            {Platform.OS === "web" && (
              <input
                accept={ACCEPT_STRING}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={handleFileSelect as any}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ref={fileInputRef as any}
                style={styles.hidden}
                type="file"
              />
            )}
          </>
        )}

        {error ? <GText style={styles.error}>{error}</GText> : null}
      </ScrollView>

      {/* Bottom button */}
      <BottomEventButton
        disabled={saving || getBottomLabel === "processing..."}
        insetBottom={insets.bottom}
        label={getBottomLabel}
        onPress={handleBottomOnPress}
        saving={saving && mode !== "view"}
        vScale={vScale}
      />

      {mode === "view" && paymentUI}

      {/* Guest list modal */}
      {mode === "view" && (
        <GuestListModal
          guests={guests}
          onClose={() => setGuestListVisible(false)}
          visible={guestListVisible}
        />
      )}

      {/* RSVP modal (no-ticket events) */}
      {mode === "view" && (
        <RsvpModal
          onClose={() => setRsvpVisible(false)}
          onSelect={handleRsvp}
          visible={rsvpVisible}
        />
      )}

      {/* Sign in modal */}
      <SignInModal
        onClose={() => setSignInVisible(false)}
        onSignedIn={() => setSignInVisible(false)}
        visible={signInVisible}
      />

      {/* Section edit modals (edit/create only) */}
      {editable && (
        <SectionEditModal
          onChange={(patch) => {
            patchValues(patch);
            handleSave(patch);
          }}
          onClose={() => setModalSection(null)}
          section={modalSection}
          values={values}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  actionText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: "600",
    marginLeft: 8,
    textAlign: "right",
  },
  bottomButton: {
    alignItems: "center",
    alignSelf: "center",
    backgroundColor: colors.white,
    borderRadius: 28,
    marginBottom: 32,
    maxWidth: 600,
    paddingHorizontal: 32,
    paddingVertical: 16,
    width: "100%",
  },
  bottomContainer: {
    bottom: 0,
    left: 0,
    paddingHorizontal: 24,
    paddingTop: 12,
    position: "absolute",
    right: 0,
  },
  bottomSpacer: {
    height: 80,
  },
  bottomText: {
    color: colors.black,
    fontSize: 18,
    fontWeight: "600",
  },
  cancelText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "600",
    width: 60,
  },
  container: {
    backgroundColor: colors.transparent,
    flex: 1,
    paddingHorizontal: HORIZONTAL_MARGIN,
  },
  content: {
    alignItems: "center",
    alignSelf: "center",
    paddingHorizontal: 24,
    width: "100%",
  },

  contentCard: {
    backgroundColor: colors.darkOverlay,
    borderRadius: 24,
    padding: 24,
    width: "100%",
  },
  description: {
    fontSize: 15,
    lineHeight: 22,
    textAlign: "left",
  },
  detailPrimary: {
    color: colors.white,
    fontSize: 18,
    fontWeight: "600",
    textAlign: "left",
  },
  detailSecondary: {
    color: colors.white,
    fontSize: 14,
    opacity: 0.8,
    textAlign: "left",
  },
  disabledOpacity: {
    opacity: 0.5,
  },
  editButton: {
    borderColor: colors.white,
    borderRadius: 16,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  editText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: "600",
  },
  error: {
    color: colors.red,
    fontSize: 14,
    marginTop: 8,
  },
  fixedHeader: {
    alignItems: "center",
    alignSelf: "center",
    flexDirection: "row",
    height: HEADER_HEIGHT,
    left: 0,
    marginHorizontal: HORIZONTAL_MARGIN,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 10,
  },
  flexFill: {
    flex: 1,
  },
  flyerLinkText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 8,
  },
  headerContainer: {
    left: 10,
    position: "absolute",
    right: 10,
    top: 0,
  },
  headerInner: {
    alignItems: "center",
    alignSelf: "center",
    flexDirection: "row",
    height: HEADER_HEIGHT,
    justifyContent: "space-between",
    maxWidth: 600,
    width: "100%",
    zIndex: 10,
  },

  headerRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: 8,
    width: "100%",
  },
  headerSpacer: {
    width: 50,
  },
  hidden: {
    display: "none",
  },
  hostedBy: {
    color: colors.light,
    fontSize: 14,
    marginBottom: 12,
    textAlign: "left",
    width: "100%",
  },
  iconColumn: {
    alignItems: "center",
    paddingRight: 10,
    width: 32,
  },
  infoGrid: {
    marginBottom: 16,
    width: "100%",
  },
  infoRow: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 12,
  },
  rowHighlight: {
    backgroundColor: colors.cardBg,
    borderRadius: 12,
  },
  rsvpNames: {
    color: colors.white,
    fontSize: 15,
    fontWeight: "600",
  },
  rsvpSection: {
    marginBottom: 16,
    width: "100%",
  },
  scroll: {
    flex: 1,
  },
  section: {
    marginBottom: 24,
    width: "100%",
  },
  statusLabel: {
    color: colors.light,
    fontSize: 14,
    fontWeight: "500",
    marginRight: 20,
  },
  statusPill: {
    borderColor: colors.divider,
    borderRadius: 20,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  statusPillActive: {
    backgroundColor: colors.white,
    borderColor: colors.white,
  },
  statusPillCancelled: {
    backgroundColor: colors.red,
    borderColor: colors.red,
  },
  statusPillDisabled: {
    opacity: 0.3,
  },
  statusPillRow: {
    flexDirection: "row",
    gap: 10,
  },
  statusPillText: {
    color: colors.white,
    fontSize: 14,
    fontWeight: "500",
  },
  statusPillTextActive: {
    color: colors.black,
  },
  statusPillTextCancelled: {
    color: colors.white,
  },
  statusRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    width: "100%",
  },
  textColumn: {
    flex: 1,
  },
  title: {
    color: colors.light,
    flex: 1,
    fontWeight: "700",
    textAlign: "left",
  },
});
